// Per-pane snapshot data context for the compare view (RES-561, plan decision D3).
//
// Each pane renders the template's real content components against its own Data
// instance: localContentData is seeded from that side's version map (frozen document
// content), while contentData is filled by a live consensus load — so data-driven
// blocks (charts, fund tables) show CURRENT imported fund data in both panes, which
// the UI declares via the "Live data — not compared" badge. Persistence is disabled:
// a compare pane must never write through /savev2.
import { Data } from '../../DataController';

// Same request shape DataController's provider implements — consensus fields are
// fetched per instrument and flattened into the eikon header/data format getValue
// expects. Trimmed copy: the compare view never uses a base/tickerGroup request.
function snapshotProvider(remote) {
	return {
		request: (data) => {
			const allPromises = data.instruments.map((instrument) =>
				Promise.resolve(remote.post(`/data/${instrument}/get`, data.fields.map((field) => field.name))));
			let hasBase = false;
			if (data.base) {
				hasBase = true;
				allPromises.unshift(Promise.resolve(remote.post(`/data/${data.base}/get`, data.fields.map((field) => field.name))));
			}
			return Promise.all(allPromises).then((responses) => {
				const eikonFormat = { data: [''], headers: [{ field: '' }] };
				for (const [index, value] of responses.entries()) {
					for (const [dataIndex] of value.headers.entries()) {
						if (responses.length > 1 && (index > 0 || !hasBase)) {
							const adjustedIndex = hasBase ? index - 1 : index;
							eikonFormat.headers.push({ field: value.headers[dataIndex].field + '<' + data.instruments[adjustedIndex] + '>' });
						} else {
							eikonFormat.headers.push({ field: value.headers[dataIndex].field });
						}
						eikonFormat.data.push(value.data[dataIndex] ? value.data[dataIndex] : '');
					}
				}
				return JSON.stringify(eikonFormat);
			});
		},
		topMessageSetter: () => { },
	};
}

export default function createSnapshotData({ remote, contentMap, templateConfig, templateName, tickers }) {
	const data = new Data({ remote });
	data.setRemote(snapshotProvider(remote));

	// Collect consensus/broker/index fields and the template type. Safe to call
	// before tickers are set: it resets tickers to [""], so its trailing
	// loadLocalData() short-circuits without hitting /retrievev2.
	data.registerTemplate(templateConfig, templateName);

	// Read-only: block persistence outright. Temporary updates stay honest —
	// they're in-memory only by contract, and some components stage computed
	// values through them for sibling blocks to read.
	data.updateLocalData = () => { };
	data.temporaryUpdateLocalData = (field, value) => {
		data.localContentData[field] = value;
	};

	data.tickers = tickers;
	data.contentData['TICKERLIST'] = tickers.join(', ');
	data.localContentData = { ...(contentMap || {}) };
	if (data.localContentData.TICKERGROUP)
		data.tickerGroup = data.localContentData.TICKERGROUP;
	data.localContentLoaded = true;

	// Live imported fund data for the data-driven blocks (async — components
	// re-render via the standard listener pattern when it lands).
	data.loadConsensusData();

	return data;
}
